import styled from 'styled-components';
import { mixins } from '@jarvis/react-portal-addons';
import { Button as VeneerButton } from '@veneer/core';

export const Container = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.color.background.container.default};
  box-sizing: border-box;
  height: fit-content;
  min-height: 392px;
  &.has-devices {
    min-height: 0;
  }
  padding: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  max-width: 1660px;
  ${({ theme }) => {
    const color = theme.color.stroke.decorative.default;
    return `
      border-bottom: 1px solid ${color};

      ${mixins.veneer.lg(`
        min-height: 536px;
        border-radius: 16px;
        border: 1px solid ${color};
      `)}
    `;
  }}
`;

export const Title = styled.h1`
  margin: 20px 24px 20px 32px;
  color: ${({ theme }) => theme.color.foreground.default};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  opacity: 1;
  transition: opacity 0.3s ease-in-out;

  &.hide {
    ${mixins.veneer.lg(`
      opacity: 0;
  `)}
  }

  &:not(.has-devices) {
    max-width: 100%;
  }

  ${mixins.veneer.lg(`
    margin: 40px 0 48px;
    margin-inline-start: 40px;
  `)}

  &.has-devices {
    ${mixins.veneer.lg(`
      position: absolute;
      top: 0;
      inset-inline-start: 0;
      margin-top: 40px;
    `)}
  }
`;

export const PrinterCarouselContainer = styled.div`
  margin: 0 0 24px 0;
  transition: margin-top 0.3s ease-in-out;
  ${mixins.veneer.sm(`
    margin: 0 0 28px  0;
  `)}
  ${mixins.veneer.lg(`
    margin: 0 0 40px 0;
  `)}
`;

// TitleAndNotificationsWrapper works in conjunction with Title and NotificationsWidgetContainer
// Together they have a very particular transition and layout behavior
// Make sure to test ALL animations upon any changes to any of those 3 components
export const TitleAndNotificationsWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  flex-direction: column;

  ${mixins.veneer.lg(`
    flex-direction: row-reverse;
  `)}

  &.no-notifications {
    ${mixins.veneer.lg(`
      flex-direction: row;
      justify-content: flex-start;
    `)}
  }


  &.has-devices {
    ${({ titleHeight, titleOverHalf }) => {
    // titleHeight = height of the title text
    // 64 = notifications height
    // 20 = top margin of the title
    // 20 = bottom margin of the title
    const height = titleHeight + 104;
    // titleHeight = height of the title text
    // 40 = top margin of the title
    // 48 = bottom margin of the title
    const heightLg = titleHeight + 88;
    const minHeightLg = titleOverHalf ? 218 : heightLg;
    return `
      min-height: ${height}px;
      ${mixins.veneer.lg(`
        min-height: ${minHeightLg}px;
      `)}
    `;
  }}}
`;

export const NotificationsWidgetContainer = styled.div`
  width: 100%;
  z-index: 9999;
  transition: max-height 0.3s ease-in-out;
  overflow: hidden;
  height: 100%;

  ${mixins.veneer.lg(`
    position: unset;
    z-index: unset;
    border-radius: 16px;
    transition: width 0.3s ease-in-out, max-width 0.3s ease-in-out, min-width 0.3s ease-in-out, max-height 0.3s ease-in-out;
    margin: 32px 32px 40px 32px;
  `)}

  &.has-devices {
    &.title-over-half {
      ${({ titleHeight }) => {
    const topPosition = 64 + titleHeight;
    return mixins.veneer.lg(`
          position: absolute;
          inset-inline-start: 0;
          top: ${topPosition}px;
          margin: 0 32px 40px 32px;`);
  }}}}

  &.collapsed {
    ${mixins.veneer.lg(`
      max-height: 64px;
      animation: unset;
      width: fit-content;
      min-width: 360px;
      max-width: 100%;
    `)}
  }

  &.expanded {
    position: fixed;
    max-height: calc(100vh - 76px);
    width: 100%;
    ${mixins.veneer.lg(`
      position: relative;
      max-height: 60vh;
      height: auto;
      width: fit-content;
      min-width: calc(100% - 64px);

      &.title-over-half {
        position: relative;
        top: 0;
        margin: 32px 32px 40px 32px;
      }
    `)}
  }
`;

export const MessageContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 70px 16px 86px 16px;
  ${mixins.veneer.sm(`
    margin: 80px 16px 96px 16px;
  `)}
  ${mixins.veneer.md(`
    margin: 82px 20px 102px 20px;
  `)}
  ${mixins.veneer.lg(`
    margin: 110px 32px 142px 32px;
  `)}
  ${mixins.veneer.xxl(`
    margin: 122px 32px 154px 32px;
  `)}

  > p {
    color: ${({ theme }) => theme.color.foreground.default};
    text-align: center;

    &:first-child {
      margin-bottom: 4px;
    }
  }

  a.inline-support-link {
    color: ${({ theme }) => theme.color.primary.base.default};
    text-decoration: underline;
    text-decoration-skip-ink: none;
    text-underline-offset: 25%;

    &:hover {
      color: ${({ theme }) => theme.color.primary.base.hover};
    }

    &:active {
      color: ${({ theme }) => theme.color.primary.base.active};
    }
  }
`;

export const Button = styled(VeneerButton)`
  && {
    margin-top: 40px;
  }
`;
